import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import { db } from "../../services/firebaseConnection";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import update from "../../assets/icons/pendencia/update.png";
import home from "../../assets/icons/bookfuncionario/turn.png";
import "./pendencia.css";

const normalizarTexto = (texto = "") =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();

const parseLocalDate = (dateString) => {
  if (!dateString) return null;

  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const getTrainingStatus = (
  trainingDate,
  validityMonths,
  possuiVencimento = true,
) => {
  if (possuiVencimento === false) return "none";
  if (!trainingDate || !validityMonths) return "none";

  try {
    const trainingDateObj = parseLocalDate(trainingDate);

    if (!trainingDateObj) return "none";

    const expirationDate = new Date(trainingDateObj);
    expirationDate.setMonth(
      expirationDate.getMonth() + parseInt(validityMonths, 10),
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);

    const timeDiff = expirationDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) return "vermelho";
    if (daysDiff <= 15) return "laranja";
    if (daysDiff <= 30) return "amarelo";

    return "none";
  } catch {
    return "none";
  }
};

const treinamentoFoiSubstituido = (trainingData) => {
  return (
    trainingData.substituido === true ||
    normalizarTexto(trainingData.statusLancamento) === "SUBSTITUIDO" ||
    normalizarTexto(trainingData.situacaoLancamento) === "SUBSTITUIDO"
  );
};

const Pendencia = () => {
  const [allTrainings, setAllTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");
  const [obraFiltro, setObraFiltro] = useState("todas");
  const [obras, setObras] = useState([]);

  const navigate = useNavigate();

  const fetchEmployeesWithTrainings = useCallback(async () => {
    try {
      setLoading(true);

      const employeesQuery = query(
        collection(db, "funcionarios"),
        orderBy("nome"),
      );

      const employeesSnapshot = await getDocs(employeesQuery);

      const employees = employeesSnapshot.docs
        .map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
          situacao: normalizarTexto(docItem.data().situacao || "ATIVO"),
          nome: normalizarTexto(docItem.data().nome || ""),
          obra: normalizarTexto(docItem.data().obra || ""),
          funcao: normalizarTexto(docItem.data().funcao || ""),
        }))
        .filter((employee) => employee.situacao === "ATIVO");

      const obrasUnicas = new Set();

      const obrasSnapshot = await getDocs(collection(db, "obras_atuais"));

      obrasSnapshot.docs.forEach((obraDoc) => {
        const dadosObra = obraDoc.data();
        const nomeObra = normalizarTexto(dadosObra.nome || "");

        if (nomeObra) {
          obrasUnicas.add(nomeObra);
        }
      });

      const trainingPromises = employees.map(async (employee) => {
        try {
          const trainingsRef = collection(
            db,
            "funcionarios",
            employee.id,
            "treinamentos",
          );

          const trainingsSnapshot = await getDocs(trainingsRef);

          return trainingsSnapshot.docs
            .map((trainingDoc) => {
              const trainingData = trainingDoc.data();

              if (treinamentoFoiSubstituido(trainingData)) {
                return null;
              }

              const possuiVencimento =
                trainingData.possuiVencimento !== undefined
                  ? Boolean(trainingData.possuiVencimento)
                  : true;

              const status = getTrainingStatus(
                trainingData.data,
                trainingData.validadeMeses,
                possuiVencimento,
              );

              if (status !== "none") {
                const obra =
                  normalizarTexto(trainingData.obraNaData) ||
                  employee.obra ||
                  "OBRA";

                if (obra) {
                  obrasUnicas.add(obra);
                }

                return {
                  id: employee.id,
                  nome:
                    normalizarTexto(trainingData.colaboradorNome) ||
                    employee.nome ||
                    "NÃO INFORMADO",
                  treinamento:
                    normalizarTexto(trainingData.treinamentoNome) ||
                    normalizarTexto(trainingData.tipo) ||
                    "NÃO INFORMADO",
                  obra,
                  funcao:
                    normalizarTexto(trainingData.funcaoNaData) ||
                    employee.funcao ||
                    "FUNÇÃO",
                  trainingDocId: trainingDoc.id,
                  data: trainingData.data || "",
                  validadeMeses: trainingData.validadeMeses || "",
                  instrutor: trainingData.avaliador
                    ? normalizarTexto(trainingData.avaliador)
                    : "--",
                  cadastradoPor: trainingData.cadastradoPor
                    ? normalizarTexto(trainingData.cadastradoPor)
                    : "--",
                  possuiVencimento,
                  status,
                  tipoLancamento:
                    normalizarTexto(trainingData.tipoLancamento) || "CADASTRO",
                };
              }

              return null;
            })
            .filter(Boolean);
        } catch (error) {
          console.error(
            `Erro ao buscar treinamentos do funcionário ${employee.id}:`,
            error,
          );
          return [];
        }
      });

      const trainingsResults = await Promise.all(trainingPromises);
      const allTrainingsData = trainingsResults.flat();

      const listaObras = Array.from(obrasUnicas).sort((a, b) =>
        a.localeCompare(b, "pt-BR"),
      );

      setAllTrainings(allTrainingsData);
      setObras(["todas", ...listaObras]);
    } catch (error) {
      console.error("Erro ao buscar treinamentos:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const resumoAlertas = useMemo(() => {
    const base =
      obraFiltro === "todas"
        ? allTrainings
        : allTrainings.filter((training) => training.obra === obraFiltro);

    return {
      vermelho: base.filter((training) => training.status === "vermelho")
        .length,
      laranja: base.filter((training) => training.status === "laranja").length,
      amarelo: base.filter((training) => training.status === "amarelo").length,
    };
  }, [allTrainings, obraFiltro]);

  const filterTrainings = useCallback(() => {
    let data = [...allTrainings];

    if (filter !== "todos") {
      data = data.filter((training) => training.status === filter);
    }

    if (obraFiltro !== "todas") {
      data = data.filter((training) => training.obra === obraFiltro);
    }

    setFilteredTrainings(data);
  }, [allTrainings, filter, obraFiltro]);

  useEffect(() => {
    fetchEmployeesWithTrainings();
  }, [fetchEmployeesWithTrainings]);

  useEffect(() => {
    filterTrainings();
  }, [filterTrainings]);

  function paginaMain() {
    navigate("/main");
  }

  function handleUpdate(employeeId, trainingType, trainingDocId) {
    navigate(
      `/adm?employeeId=${employeeId}&trainingType=${encodeURIComponent(
        trainingType,
      )}&trainingDocId=${encodeURIComponent(trainingDocId || "")}`,
    );
  }

  if (loading) {
    return (
      <div className="container-pendencia">
        <div className="container-pendencia-total">
          <div className="loading-message">
            <p>CARREGANDO TREINAMENTOS...</p>
          </div>
        </div>

        <div className="home-pendencia">
          <img
            className="icon-home-pendencia"
            onClick={paginaMain}
            src={home}
            alt="Voltar para a tela principal"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container-pendencia">
      <div className="container-pendencia-total">
        <div className="pendencia-topo-fixo">
          <div className="topo-controles-pendencia">
            <div className="filtros-container">
              <button
                className={filter === "todos" ? "filtro-ativo" : "filtro"}
                onClick={() => setFilter("todos")}
              >
                TODOS
              </button>

              <button
                className={filter === "vermelho" ? "filtro-ativo" : "filtro"}
                onClick={() => setFilter("vermelho")}
              >
                VERMELHO
              </button>

              <button
                className={filter === "laranja" ? "filtro-ativo" : "filtro"}
                onClick={() => setFilter("laranja")}
              >
                LARANJA
              </button>

              <button
                className={filter === "amarelo" ? "filtro-ativo" : "filtro"}
                onClick={() => setFilter("amarelo")}
              >
                AMARELO
              </button>

              <select
                className="select-obra"
                value={obraFiltro}
                onChange={(e) => setObraFiltro(e.target.value)}
              >
                {obras.map((obra, index) => (
                  <option key={`${obra}-${index}`} value={obra}>
                    {obra === "todas" ? "TODAS AS OBRAS" : obra}
                  </option>
                ))}
              </select>
            </div>

            <div className="legenda-contadores">
              <div className="legenda-item">
                <span className="bolinha-legenda vermelho"></span>
                <strong>VENCIDO</strong>
                <div className="contador-legenda">{resumoAlertas.vermelho}</div>
              </div>

              <div className="legenda-item">
                <span className="bolinha-legenda amarelo"></span>
                <strong>30 DIAS PARA VENCER</strong>
                <div className="contador-legenda">{resumoAlertas.amarelo}</div>
              </div>

              <div className="legenda-item">
                <span className="bolinha-legenda laranja"></span>
                <strong>15 DIAS PARA VENCER</strong>
                <div className="contador-legenda">{resumoAlertas.laranja}</div>
              </div>
            </div>
          </div>

          <div className="pendencia-header">
            <div className="header-box nome">NOME</div>
            <div className="header-box treinamento">TREINAMENTO</div>
            <div className="header-box obra">OBRA</div>
            <div className="header-box instrutor">INSTRUTOR</div>
            <div className="header-box lancado">LANÇADO POR</div>
            <div className="header-box acao">AÇÃO</div>
          </div>
        </div>

        <div className="pendencia-list">
          {filteredTrainings.length > 0 ? (
            filteredTrainings.map((training) => (
              <div
                key={`${training.id}-${training.trainingDocId}`}
                className={`pendencia-item ${training.status}`}
              >
                <p className="col nome">{training.nome}</p>
                <p className="col treinamento">{training.treinamento}</p>
                <p className="col obra">{training.obra}</p>
                <p className="col instrutor">{training.instrutor}</p>
                <p className="col lancado">{training.cadastradoPor}</p>

                <div
                  onClick={() =>
                    handleUpdate(
                      training.id,
                      training.treinamento,
                      training.trainingDocId,
                    )
                  }
                  className="btn-atualizar col acao"
                >
                  <span>ATUALIZAR</span>
                  <img src={update} alt="Atualizar treinamento" />
                </div>
              </div>
            ))
          ) : (
            <div className="pendencia-item">
              <p style={{ width: "100%", textAlign: "center" }}>
                {filter === "todos"
                  ? "NENHUM TREINAMENTO COM ALERTA ENCONTRADO"
                  : `NENHUM TREINAMENTO COM ALERTA ${filter.toUpperCase()} ENCONTRADO`}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="home-pendencia">
        <img
          className="icon-home-pendencia"
          onClick={paginaMain}
          src={home}
          alt="Voltar para a tela principal"
        />
      </div>
    </div>
  );
};

export default Pendencia;
